import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const GuestBookForm = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    idNumber: '',
    isFamilyMember: false,
    familyMemberType: '',
    fatherBranch: '',
    martyrTiming: '',
    acceptedTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'en';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'en';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const formContent = {
    ar: {
      title: "وقع في دفتر الضيوف",
      description: "اترك رسالة تذكر وكن جزءاً من التاريخ",
      nameLabel: "الاسم الكامل",
      namePlaceholder: "أدخل اسمك الكامل",
      emailLabel: "البريد الإلكتروني",
      emailPlaceholder: "أدخل بريدك الإلكتروني",
      locationLabel: "المدينة",
      locationPlaceholder: "أدخل مدينتك",
      countryLabel: "البلد",
      countryPlaceholder: "اختر بلدك",
      messageLabel: "رسالة التذكر",
      messagePlaceholder: "شارك رسالتك أو ذكرياتك...",
      submitButton: "إضافة التوقيع",
      submittingButton: "جاري الإرسال...",
      successTitle: "شكراً لك!",
      successMessage: "تم إضافة توقيعك بنجاح إلى دفتر الضيوف. رسالتك ستكون جزءاً من التاريخ.",
      signatureCount: "توقيع",
      requiredError: "هذا الحقل مطلوب",
      emailError: "يرجى إدخال بريد إلكتروني صحيح",
      phoneLabel: "رقم الهاتف",
      phonePlaceholder: "أدخل رقم هاتفك",
      phoneError: "يرجى إدخال رقم هاتف صحيح",
      phoneLimitError: "تم استخدام هذا الرقم 3 مرات بالفعل. الحد الأقصى المسموح به هو 3 إرسالات.",
      idNumberLabel: "رقم الهوية",
      idNumberPlaceholder: "أدخل رقم هويتك",
      idNumberError: "يرجى إدخال رقم هوية صحيح",
      submitError: "حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.",
      numberOfAttendantsLabel: "عدد المرافقين",
      numberOfAttendantsPlaceholder: "كم عدد الأشخاص الذين سيرافقونك؟",
      isFamilyMemberLabel: "هل أنت من عائلة شهيد أو معتقل أو مُغَيَّب؟",
      familyMemberTypeLabel: "ما نوع الحالة؟",
      familyMemberTypePlaceholder: "اختر نوع الحالة",
      fatherBranchLabel: "ما كان فرع والدك؟",
      fatherBranchPlaceholder: "اختر فرع والدك",
      martyrTimingLabel: "هل كان ذلك قبل معركة رد العدوان أم بعدها؟",
      martyrTimingPlaceholder: "اختر التوقيت",
      acceptedTermsLabel: "أوافق على الشروط والأحكام التركية لجمع المعلومات واستخدامها",
      acceptedTermsError: "يجب الموافقة على الشروط والأحكام"
    },
    en: {
      title: "Sign the Guest Book",
      description: "Leave a message of remembrance and be part of history",
      nameLabel: "Full Name",
      namePlaceholder: "Enter your full name",
      emailLabel: "Email Address",
      emailPlaceholder: "Enter your email address",
      locationLabel: "City",
      locationPlaceholder: "Enter your city",
      countryLabel: "Country",
      countryPlaceholder: "Select your country",
      messageLabel: "Message of Remembrance",
      messagePlaceholder: "Share your message or memories...",
      submitButton: "Add Signature",
      submittingButton: "Submitting...",
      successTitle: "Thank You!",
      successMessage: "Your signature has been successfully added to the guest book. Your message will be part of history.",
      signatureCount: "signatures",
      requiredError: "This field is required",
      emailError: "Please enter a valid email address",
      phoneLabel: "Phone Number",
      phonePlaceholder: "Enter your phone number",
      phoneError: "Please enter a valid phone number",
      phoneLimitError: "This phone number has already been used 3 times. Maximum allowed is 3 submissions.",
      idNumberLabel: "ID Number",
      idNumberPlaceholder: "Enter your ID number",
      idNumberError: "Please enter a valid ID number",
      submitError: "Failed to submit. Please try again.",
      numberOfAttendantsLabel: "Number of Attendants",
      numberOfAttendantsPlaceholder: "How many are you bringing with you?",
      isFamilyMemberLabel: "Are you a family member of a martyr, detained, or absent person?",
      familyMemberTypeLabel: "What is the status?",
      familyMemberTypePlaceholder: "Select the status",
      fatherBranchLabel: "What was your father's branch?",
      fatherBranchPlaceholder: "Select your father's branch",
      martyrTimingLabel: "If he was a martyr, was it before or after the battle of Rad' al-'udwān?",
      martyrTimingPlaceholder: "Select timing",
      acceptedTermsLabel: "I accept the Turkish terms and conditions for information collection and usage",
      acceptedTermsError: "You must accept the terms and conditions"
    },
    tr: {
      title: "Ziyaretçi Defterini İmzala",
      description: "Anma mesajınızı bırakın ve tarihin bir parçası olun",
      nameLabel: "Ad Soyad",
      namePlaceholder: "Adınızı ve soyadınızı girin",
      emailLabel: "E-posta Adresi",
      emailPlaceholder: "E-posta adresinizi girin",
      locationLabel: "Şehir",
      locationPlaceholder: "Şehrinizi girin",
      countryLabel: "Ülke",
      countryPlaceholder: "Ülkenizi seçin",
      messageLabel: "Anma Mesajı",
      messagePlaceholder: "Mesajınızı veya anılarınızı paylaşın...",
      submitButton: "İmza Ekle",
      submittingButton: "Gönderiliyor...",
      successTitle: "Teşekkürler!",
      successMessage: "İmzanız başarıyla ziyaretçi defterine eklendi. Mesajınız tarihin bir parçası olacak.",
      signatureCount: "imza",
      requiredError: "Bu alan gereklidir",
      emailError: "Lütfen geçerli bir e-posta adresi girin",
      phoneLabel: "Telefon Numarası",
      phonePlaceholder: "Telefon numaranızı girin",
      phoneError: "Lütfen geçerli bir telefon numarası girin",
      phoneLimitError: "Bu telefon numarası zaten 3 kez kullanıldı. Maksimum izin verilen 3 gönderimdir.",
      idNumberLabel: "Kimlik Numarası",
      idNumberPlaceholder: "Kimlik numaranızı girin",
      idNumberError: "Lütfen geçerli bir kimlik numarası girin",
      submitError: "Gönderim başarısız oldu. Lütfen tekrar deneyin.",
      numberOfAttendantsLabel: "Katılımcı Sayısı",
      numberOfAttendantsPlaceholder: "Kaç kişi getiriyorsunuz?",
      isFamilyMemberLabel: "Bir şehidin, tutuklunun veya kayıp kişinin aile üyesi misiniz?",
      familyMemberTypeLabel: "Durum nedir?",
      familyMemberTypePlaceholder: "Durumu seçin",
      fatherBranchLabel: "Babanızın branşı neydi?",
      fatherBranchPlaceholder: "Babanızın branşını seçin",
      martyrTimingLabel: "Eğer şehitse, Rad' al-'udwān savaşından önce mi yoksa sonra mı?",
      martyrTimingPlaceholder: "Zamanlamayı seçin",
      acceptedTermsLabel: "Bilgi toplama ve kullanım için Türk şartlarını ve koşullarını kabul ediyorum",
      acceptedTermsError: "Şartları ve koşulları kabul etmelisiniz"
    }
  };

  const fatherBranchLabels = {
    ar: {
      security_forces: 'القوات الأمنية',
      army: 'الجيش',
      civil_defense: 'الدفاع المدني'
    },
    en: {
      security_forces: 'Security Forces',
      army: 'Army',
      civil_defense: 'Civil Defense'
    },
    tr: {
      security_forces: 'Güvenlik Kuvvetleri',
      army: 'Ordu',
      civil_defense: 'Sivil Savunma'
    }
  };

  const martyrTimingLabels = {
    ar: {
      before: 'قبل رد العدوان',
      after: 'بعد رد العدوان'
    },
    en: {
      before: 'Before Rad\' al-\'udwān',
      after: 'After Rad\' al-\'udwān'
    },
    tr: {
      before: 'Rad\' al-\'udwān\'dan Önce',
      after: 'Rad\' al-\'udwān\'dan Sonra'
    }
  };

  const familyMemberTypeLabels = {
    ar: {
      martyr: 'شهيد',
      detained: 'معتقل',
      absent: 'مُغَيَّب'
    },
    en: {
      martyr: 'Martyr',
      detained: 'Detained',
      absent: 'Absent'
    },
    tr: {
      martyr: 'Şehit',
      detained: 'Tutuklu',
      absent: 'Kayıp'
    }
  };

  const getFatherBranchOptions = () => {
    const labels = fatherBranchLabels[currentLanguage] || fatherBranchLabels.en;
    return [
      { value: 'security_forces', label: labels.security_forces },
      { value: 'army', label: labels.army },
      { value: 'civil_defense', label: labels.civil_defense }
    ];
  };

  const getMartyrTimingOptions = () => {
    const labels = martyrTimingLabels[currentLanguage] || martyrTimingLabels.en;
    return [
      { value: 'before', label: labels.before },
      { value: 'after', label: labels.after }
    ];
  };

  const getFamilyMemberTypeOptions = () => {
    const labels = familyMemberTypeLabels[currentLanguage] || familyMemberTypeLabels.en;
    return [
      { value: 'martyr', label: labels.martyr },
      { value: 'detained', label: labels.detained },
      { value: 'absent', label: labels.absent }
    ];
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const content = formContent?.[currentLanguage];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = content?.requiredError;
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = content?.requiredError;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = content?.emailError;
    }
    
    if (!formData?.phone?.trim()) {
      newErrors.phone = content?.requiredError;
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = content?.phoneError;
    }

    if (!formData?.idNumber?.trim()) {
      newErrors.idNumber = content?.requiredError;
    }

    if (formData?.isFamilyMember) {
      if (!formData?.familyMemberType) {
        newErrors.familyMemberType = content?.requiredError;
      }
      if (!formData?.fatherBranch) {
        newErrors.fatherBranch = content?.requiredError;
      }
      if (formData?.familyMemberType === 'martyr' && !formData?.martyrTiming) {
        newErrors.martyrTiming = content?.requiredError;
      }
    }

    if (!formData?.acceptedTerms) {
      newErrors.acceptedTerms = content?.acceptedTermsError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Helper function to convert English keys to Arabic labels
  const getArabicLabel = (key, labelType) => {
    if (!key) return '';
    
    if (labelType === 'familyMemberType') {
      return familyMemberTypeLabels.ar[key] || key;
    } else if (labelType === 'fatherBranch') {
      return fatherBranchLabels.ar[key] || key;
    } else if (labelType === 'martyrTiming') {
      return martyrTimingLabels.ar[key] || key;
    }
    return key;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setErrors({}); // Clear previous errors
    
    try {
      // Get API token from environment variable
      const apiToken = import.meta.env.VITE_API_SECRET_TOKEN || '';
      
      // Prepare submission data - convert English keys to Arabic labels
      const submissionData = {
        formType: 'guestbook',
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        idNumber: formData.idNumber || '',
        isFamilyMember: formData.isFamilyMember || false,
        familyMemberType: getArabicLabel(formData.familyMemberType, 'familyMemberType'),
        fatherBranch: getArabicLabel(formData.fatherBranch, 'fatherBranch'),
        martyrTiming: getArabicLabel(formData.martyrTiming, 'martyrTiming'),
        acceptedTerms: formData.acceptedTerms || false,
        token: apiToken, // Include API token for authentication
        origin: window.location.origin // Include origin for CORS validation
      };

      // Get Google Script URL from environment variable
      const scriptUrl = import.meta.env.VITE_GOOGLE_SCRIPT_URL || '';
      
      if (!scriptUrl) {
        throw new Error('Google Script URL not configured');
      }
      
      // Send to Google Apps Script
      // CORS is required - no fallback to no-cors mode
      const response = await fetch(scriptUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      // Parse response
      const result = await response.json();
      
      // Check if submission was successful
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit. Please try again.');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after success
      setTimeout(() => {
        setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            idNumber: '',
            isFamilyMember: false,
            familyMemberType: '',
            fatherBranch: '',
            martyrTiming: '',
            acceptedTerms: false
          });
      }, 3000);
      
    } catch (error) {
      console.error('Failed to submit:', error);
      setIsSubmitting(false);
      
      const errorMessage = error.message || 
                          content?.submitError || 
                          'Failed to submit. Please try again.';
      
      // Check if it's a phone limit error
      if (error.message && (error.message.includes('phone number') || error.message.includes('limit exceeded') || error.message.includes('already been used'))) {
        setErrors({ 
          phone: content?.phoneLimitError || errorMessage
        });
      } else {
        setErrors({ 
          submit: errorMessage
        });
      }
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Check" size={32} className="text-success" />
        </div>
        <h3 className="font-body text-xl font-semibold text-primary mb-2">
          {content?.successTitle}
        </h3>
        <p className="font-body text-text-secondary mb-6">
          {content?.successMessage}
        </p>
        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-3">
            <Icon name="Users" size={20} className="text-success" />
            <span className="font-body text-sm text-success font-medium">
              1,247 {content?.signatureCount}
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div>
      <div className="text-center mb-8">
        <h3 className="font-body text-2xl font-semibold text-primary mb-2">
          {content?.title}
        </h3>
        <p className="font-body text-text-secondary">
          {content?.description}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={content?.nameLabel}
            type="text"
            placeholder={content?.namePlaceholder}
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Input
            label={content?.emailLabel}
            type="email"
            placeholder={content?.emailPlaceholder}
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={content?.phoneLabel}
            type="number"
            placeholder={content?.phonePlaceholder}
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
            required
          />

          <Input
            label={content?.idNumberLabel}
            type="text"
            placeholder={content?.idNumberPlaceholder}
            value={formData?.idNumber}
            onChange={(e) => handleInputChange('idNumber', e?.target?.value)}
            error={errors?.idNumber}
            required
          />
        </div>

        <div className="space-y-4 border-t border-border pt-6">
          <Checkbox
            label={content?.isFamilyMemberLabel}
            checked={formData?.isFamilyMember}
            onChange={(e) => {
              handleInputChange('isFamilyMember', e?.target?.checked);
              // Clear related fields if unchecked
              if (!e?.target?.checked) {
                handleInputChange('familyMemberType', '');
                handleInputChange('fatherBranch', '');
                handleInputChange('martyrTiming', '');
              }
            }}
          />

          {formData?.isFamilyMember && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4 pl-6 border-l-2 border-primary/20"
            >
              <Select
                label={content?.familyMemberTypeLabel}
                placeholder={content?.familyMemberTypePlaceholder}
                options={getFamilyMemberTypeOptions()}
                value={formData?.familyMemberType}
                onChange={(value) => {
                  handleInputChange('familyMemberType', value);
                  // Clear martyr timing if type changes away from martyr
                  if (value !== 'martyr') {
                    handleInputChange('martyrTiming', '');
                  }
                }}
                error={errors?.familyMemberType}
                required
              />

              <Select
                label={content?.fatherBranchLabel}
                placeholder={content?.fatherBranchPlaceholder}
                options={getFatherBranchOptions()}
                value={formData?.fatherBranch}
                onChange={(value) => handleInputChange('fatherBranch', value)}
                error={errors?.fatherBranch}
                required
              />

              {formData?.familyMemberType === 'martyr' && (
                <Select
                  label={content?.martyrTimingLabel}
                  placeholder={content?.martyrTimingPlaceholder}
                  options={getMartyrTimingOptions()}
                  value={formData?.martyrTiming}
                  onChange={(value) => handleInputChange('martyrTiming', value)}
                  error={errors?.martyrTiming}
                  required
                />
              )}
            </motion.div>
          )}
        </div>

        <div>
          <Checkbox
            label={content?.acceptedTermsLabel}
            checked={formData?.acceptedTerms}
            onChange={(e) => handleInputChange('acceptedTerms', e?.target?.checked)}
            error={errors?.acceptedTerms}
            required
          />
        </div>

        {errors?.submit && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <p className="font-body text-sm text-error text-center">
              {errors.submit}
            </p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isSubmitting}
          disabled={isSubmitting}
          iconName="PenTool"
          iconPosition="left"
          fullWidth
        >
          {isSubmitting ? content?.submittingButton : content?.submitButton}
        </Button>
      </form>
      {/* Signature Counter */}
      <div className="mt-8 text-center">
        <div className="inline-flex items-center space-x-3 bg-muted rounded-lg px-4 py-2">
          <Icon name="Users" size={16} className="text-primary" />
          <span className="font-body text-sm text-text-secondary">
            1,247 {content?.signatureCount}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GuestBookForm;